import React, { useEffect, useRef, useState } from 'react';
import { DatabaseSoundPack, DatabaseSoundPackModel } from '../../services/DatabaseSoundPack';
import PackArtwork from './PackArtwork';

// A horizontally-scrolling "Browse Packs" row of pack cards: square artwork,
// then the name — optionally a link, with the `secondaryTitle`
// on its own line — and a caption line (the formatted price by default; override
// with `caption`, e.g. "Included" for subscribers). `overlayAction` floats a
// control on the cover (the cart's add button). `action` is a right-aligned slot
// in the heading row ("See all"). On hover, slim scroll panels slide in flush to
// each rail edge and hide once that end is reached. Renders nothing when empty.
export function PackScroller({
    title,
    packs,
    coverImages,
    caption = (pack) => DatabaseSoundPackModel.formatPrice(pack.price),
    packHref,
    overlayAction,
    action,
    className = '',
}: {
    title: React.ReactNode;
    packs: DatabaseSoundPack[];
    coverImages: Record<string, string>;
    caption?: (pack: DatabaseSoundPack) => React.ReactNode;
    packHref?: (pack: DatabaseSoundPack) => string | undefined;
    overlayAction?: (pack: DatabaseSoundPack) => React.ReactNode;
    action?: React.ReactNode;
    className?: string;
}) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [rail, setRail] = useState({ atStart: true, atEnd: false });
    const [coverH, setCoverH] = useState<number>();

    const syncRail = () => {
        const el = scrollRef.current;
        if (!el) return;
        const atStart = el.scrollLeft <= 8;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
        setRail((prev) => (prev.atStart === atStart && prev.atEnd === atEnd ? prev : { atStart, atEnd }));
    };

    useEffect(() => {
        const measure = () => {
            const cover = scrollRef.current?.querySelector<HTMLElement>('[data-cover]');
            if (cover) {
                const h = Math.round(cover.getBoundingClientRect().height);
                setCoverH((prev) => (prev === h || !h ? prev : h));
            }
            syncRail();
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packs.length]);

    const scrollByDir = (dir: 1 | -1) => {
        const el = scrollRef.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
    };

    if (packs.length === 0) return null;

    const panelHeight = coverH ? `${coverH}px` : 'calc(100% - 71px)';
    const panelBase =
        'absolute top-0 z-10 flex w-7 items-center justify-center bg-white/90 backdrop-blur-[2px] transition-opacity duration-150';

    return (
        <section className={className}>
            <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-heading font-bold text-ink">{title}</h2>
                {action}
            </div>

            <div className="group relative mt-5">
                <button
                    type="button"
                    aria-label="Scroll packs back"
                    onClick={() => scrollByDir(-1)}
                    style={{ height: panelHeight }}
                    className={`${panelBase} left-0 rounded-r-lg border-r border-hairline ${
                        rail.atStart ? 'pointer-events-none opacity-0' : 'opacity-0 group-hover:opacity-100'
                    }`}
                >
                    <svg width="8" height="12" viewBox="0 0 8 12" aria-hidden>
                        <path d="M7 1 1 6l6 5Z" fill="#000" />
                    </svg>
                </button>
                <button
                    type="button"
                    aria-label="Scroll packs forward"
                    onClick={() => scrollByDir(1)}
                    style={{ height: panelHeight }}
                    className={`${panelBase} right-0 rounded-l-lg border-l border-hairline ${
                        rail.atEnd ? 'pointer-events-none opacity-0' : 'opacity-0 group-hover:opacity-100'
                    }`}
                >
                    <svg width="8" height="12" viewBox="0 0 8 12" aria-hidden>
                        <path d="M1 1l6 5-6 5Z" fill="#000" />
                    </svg>
                </button>

                <div
                    ref={scrollRef}
                    onScroll={syncRail}
                    className="flex gap-4 overflow-x-auto pb-2 [scrollbar-color:#DEDCD3_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#DEDCD3] [&::-webkit-scrollbar]:h-1.5"
                >
                    {packs.map((pack) => {
                        const href = packHref?.(pack);
                        const nameInner = (
                            <>
                                <span className="truncate text-r-sub font-bold leading-[1.3]">{pack.name}</span>
                                {pack.secondaryTitle && (
                                    <span className="truncate text-r-sub font-bold leading-[1.3] text-[#A5A29A]">
                                        {pack.secondaryTitle}
                                    </span>
                                )}
                            </>
                        );
                        return (
                            <div key={pack.id} className="flex w-[150px] flex-none flex-col gap-[9px]">
                                <div data-cover className="relative aspect-square">
                                    <PackArtwork
                                        src={pack.id ? coverImages[pack.id] : undefined}
                                        alt={`${pack.name} cover`}
                                        border="subtle"
                                        className="absolute inset-0 h-full w-full"
                                    />
                                    {overlayAction && (
                                        <div className="absolute bottom-[7px] right-[7px]">{overlayAction(pack)}</div>
                                    )}
                                </div>
                                <div className="flex min-w-0 flex-col">
                                    {href ? (
                                        <a
                                            href={href}
                                            className="flex flex-col text-ink no-underline hover:underline"
                                        >
                                            {nameInner}
                                        </a>
                                    ) : (
                                        <div className="flex flex-col text-ink">{nameInner}</div>
                                    )}
                                    <span className="mt-[3px] text-r-cap text-subtle">{caption(pack)}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export default PackScroller;
